import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicFormComponent } from 'src/app/shared/components/dynamic-form/dynamic-form.component';
import { GoogleRecaptchaService } from 'src/app/shared/services/google-recaptcha/google-recaptcha.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import {
  DEFAULT_PROJECT_TIMEZONE,
  PROJECT_NAME_ALLOWED_PATTERN,
  PROJECT_NAME_DUPLICATE_MESSAGE,
  PROJECT_NAME_FORBIDDEN_MESSAGE,
  PROJECT_NAME_MAX_LENGTH_MESSAGE,
  PROJECT_NAME_MIN_LENGTH_MESSAGE,
  PROJECT_NAME_REQUIRED_MESSAGE,
  normalizeCountrySelection,
  normalizeProjectName,
  toCountryOptions,
  validateProjectNameLive
} from '../project-form.util';

@Component({
  selector: 'app-clone-existing-project',
  templateUrl: './clone-existing-project.component.html',
  styleUrls: ['./clone-existing-project.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, DynamicFormComponent]
})
export class CloneExistingProjectComponent implements OnInit {
  @Output() projectCloned = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  @ViewChild(DynamicFormComponent) dynamicForm?: DynamicFormComponent;

  formSchema: any = null;
  formData: any = null;
  isSubmitting: boolean = false;
  recaptchaToken: string | null = null;
  existingProjects: any[] = [];
  existingProjectNames: string[] = [];
  countries: any[] = [];
  hierarchySearchEnabled: boolean = false;

  constructor(
    private googleRecaptcha: GoogleRecaptchaService,
    private toast: ToastService,
    private accountsService: AccountsService,
    private appStateService: AppStateService
  ) {}

  ngOnInit(): void {
    this.countries = toCountryOptions();
    this.loadExistingProjects();
    this.buildFormSchema();
    this.initializeRecaptcha();
  }

  private initializeRecaptcha(): void {
    this.googleRecaptcha.initializeToken().subscribe(token => {
      this.recaptchaToken = token;
    });
  }

  get showHierarchySearch(): boolean {
    return !!this.formData?.clone?.dynamic_hierarchy;
  }

  private loadExistingProjects(): void {
    const accountDetails: any = this.appStateService.accountDetails();
    const accountId = accountDetails?._id || localStorage.getItem('LtMsAcTiDfR');
    if (!accountId) {
      return;
    }

    this.accountsService.getBrandsList(accountId).subscribe({
      next: (response) => {
        if (response && response.data && response.data.brands) {
          const brands = response.data.brands;
          this.existingProjectNames = brands.map((brand: any) => brand.name);
          this.existingProjects = brands.map((brand: any) => ({
            label: brand.name,
            value: brand._id
          }));
          this.buildFormSchema();
        }
      },
      error: () => {
        this.toast.showPopup('ERROR', 'Error', 'We are facing problem while connecting to the server. Please check your internet connection or try again after sometime.', 1500);
      }
    });
  }

  private buildFormSchema(): void {
    this.formSchema = {
      type: 'object',
      properties: {
        project_name: {
          label: 'Project Name',
          type: 'string',
          minLength: 3,
          maxLength: 50,
          placeholder: 'New Project Name',
          pattern: PROJECT_NAME_ALLOWED_PATTERN,
          customValidation: (value: string) => validateProjectNameLive(value, this.existingProjectNames),
          errorMessages: {
            required: PROJECT_NAME_REQUIRED_MESSAGE,
            minLength: PROJECT_NAME_MIN_LENGTH_MESSAGE,
            maxLength: PROJECT_NAME_MAX_LENGTH_MESSAGE,
            pattern: PROJECT_NAME_FORBIDDEN_MESSAGE,
            custom: PROJECT_NAME_DUPLICATE_MESSAGE
          }
        },
        existing_project: {
          label: 'Existing Project',
          type: 'string',
          input: 'dropdown',
          options: this.existingProjects,
          optionLabel: 'label',
          optionValue: 'value',
          placeholder: 'Select Existing Project',
          errorMessages: {
            required: 'Please select a project to clone'
          }
        },
        clone_options: {
          label: 'Clone',
          type: 'object',
          group: [
            {
              label: 'Conversation Questions',
              type: 'boolean',
              input: 'checkbox',
              default: false
            },
            {
              label: 'User Notifications',
              type: 'boolean',
              input: 'checkbox',
              default: false
            },
            {
              label: 'Dynamic Hierarchy',
              type: 'boolean',
              input: 'checkbox',
              default: false
            }
          ]
        },
        audience_countries: {
          label: 'Audience Countries',
          type: 'array',
          input: 'multi-select',
          widget: 'multi-select',
          options: this.countries,
          optionLabel: 'label',
          optionValue: 'value',
          placeholder: 'Select a country',
          default: ['India'],
          description: 'This field indicates one or multiple audience countries for which this project is created for.'
        }
      },
      required: ['project_name', 'existing_project', 'clone_options']
    };
  }

  onFormChange(event: { value: any; valid: boolean }): void {
    this.formData = event.value;
    if (!this.showHierarchySearch) {
      this.hierarchySearchEnabled = false;
    }
  }

  onFormSubmit(formValue: any): void {
    if (this.isSubmitting) {
      return;
    }

    const projectName = normalizeProjectName(formValue.project_name);
    const nameCheck = validateProjectNameLive(projectName, this.existingProjectNames);
    if (!projectName || !nameCheck.valid) {
      this.toast.showPopup(
        'ERROR',
        'Invalid Project Name',
        nameCheck.message || (projectName ? PROJECT_NAME_DUPLICATE_MESSAGE : PROJECT_NAME_REQUIRED_MESSAGE),
        3000
      );
      return;
    }

    const accountDetails: any = this.appStateService.accountDetails();
    const accountId = accountDetails?._id || localStorage.getItem('LtMsAcTiDfR');
    if (!accountId) {
      this.toast.showPopup('ERROR', 'Account Error', 'Account ID not found. Please refresh the page and try again.', 3000);
      return;
    }

    if (!formValue.existing_project) {
      this.toast.showPopup('ERROR', 'Error', 'Please select a project to clone', 3000);
      return;
    }

    if (!this.hasCloneSelection(formValue)) {
      this.toast.showPopup('ERROR', 'Error', 'Please select at least one clone option', 3000);
      return;
    }

    this.isSubmitting = true;
    this.googleRecaptcha.initializeToken().subscribe(token => {
      if (!token) {
        this.isSubmitting = false;
        this.toast.showPopup('ERROR', 'reCAPTCHA Required', 'Please complete the reCAPTCHA verification.', 3000);
        return;
      }
      this.recaptchaToken = token;
      this.cloneProject(formValue, accountId, projectName);
    });
  }

  private cloneProject(formValue: any, accountId: string, projectName: string): void {
    const cloneOptions = this.getCloneOptions(formValue);
    const audienceCountries = normalizeCountrySelection(
      formValue.audience_countries || formValue.audience_country
    );
    const cloneProjectData: any = {
      account_id: accountId,
      brand_name: projectName,
      project_type: 1,
      timezone: { ...DEFAULT_PROJECT_TIMEZONE },
      is_single_touchpoint_setup: true,
      dashboard_version: 3,
      g_recaptcha_response_v3: this.recaptchaToken
      // g_recaptcha_response: 'litmusworld_internal'
    };

    if (audienceCountries.length) {
      cloneProjectData.audience_countries = audienceCountries;
    }
    if (this.hierarchySearchEnabled) {
      cloneProjectData.is_hierarchy_search_enabled = true;
    }

    this.accountsService.createBrand(cloneProjectData).subscribe({
      next: (response) => {
        const payload = response?.data || response;
        const code = payload?.code ?? response?.code;
        if (code === 0 || code === 401 || code === 406) {
          const targetBrandId = payload.brand_id;
          const cloneReqData: any = {
            brand_id: targetBrandId,
            job_type: 6,
            parameters: {
              source_brand_id: formValue.existing_project,
              target_brand_id: targetBrandId,
              interaction: !!cloneOptions.conversation_questions,
              hierarchy: !!cloneOptions.dynamic_hierarchy,
              notification: !!cloneOptions.user_notifications
            }
          };
          if (cloneOptions.dynamic_hierarchy) {
            cloneReqData.parameters.dynamic_hierarchy = true;
          }
          if (this.hierarchySearchEnabled) {
            cloneReqData.parameters.is_hierarchy_search_enabled = true;
          }

          this.accountsService.createLitmusJobs(cloneReqData).subscribe({
            next: (jobResponse) => {
              this.isSubmitting = false;
              const jobCode = jobResponse?.code ?? jobResponse?.data?.code;
              if (jobCode === 0 || jobCode === 401 || jobCode === 406) {
                this.toast.showPopup('SUCCESS', 'Success', 'Cloned project has been created successfully', 3000);
                this.projectCloned.emit({
                  brand_id: targetBrandId,
                  brand_name: projectName
                });
                this.recaptchaToken = null;
                this.initializeRecaptcha();
              } else {
                this.toast.showPopup(
                  'ERROR',
                  'Error',
                  jobResponse?.error_message || jobResponse?.data?.error_message || 'Something went wrong',
                  3000
                );
                this.recaptchaToken = null;
                this.initializeRecaptcha();
              }
            },
            error: () => {
              this.isSubmitting = false;
              this.toast.showPopup('ERROR', 'Error', 'Something went wrong', 3000);
              this.recaptchaToken = null;
              this.initializeRecaptcha();
            }
          });
        } else {
          this.isSubmitting = false;
          this.toast.showPopup(
            'ERROR',
            'Error',
            payload?.error_message || payload?.message || 'Something went wrong',
            3000
          );
          this.recaptchaToken = null;
          this.initializeRecaptcha();
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        this.recaptchaToken = null;
        this.initializeRecaptcha();
        let errorMessage = 'Something went wrong';
        if (error?.error?.data?.code === 4052) {
          errorMessage = 'Error updating account for the created brand. The account may not exist or you may not have permission.';
        } else if (error?.error?.data?.code === 4002) {
          errorMessage = 'Account details not found. Please refresh and try again.';
        } else if (error?.error?.data?.error_message || error?.error?.message) {
          errorMessage = error.error.data?.error_message || error.error.message;
        }
        this.toast.showPopup('ERROR', 'Error', errorMessage, 5000);
      }
    });
  }

  submit(): void {
    this.dynamicForm?.onSubmit();
  }

  canSubmit(): boolean {
    return !this.isSubmitting && !!this.dynamicForm?.isFormReady() && this.hasCloneSelection();
  }

  private getCloneOptions(formValue?: any): any {
    const value = formValue || this.formData || this.dynamicForm?.form?.getRawValue() || {};
    return value.clone || value.clone_options || {};
  }

  private hasCloneSelection(formValue?: any): boolean {
    const clone = this.getCloneOptions(formValue);
    return !!(clone.conversation_questions || clone.user_notifications || clone.dynamic_hierarchy);
  }

  onCancel(): void {
    this.cancel.emit();
  }
}
